import BaseModel from './BaseModel';
import { Moment } from 'moment';
import Project from './Project';
import Change from './Change';
import Comment from './Comment';
import Quote from './Quote';
import Commit from './Commit';
import Release from './Release';
import User from './User';
import moment from 'moment';
import { t } from 'i18next';
import { i18n, formatMomentDate } from '../locales';

/** class representing a log */
class Log extends BaseModel {

    static TYPE_CHANGE_CREATE                 = 'change:create';
    static TYPE_CHANGE_PATCH                  = 'change:patch';
    static TYPE_CHANGE_ESTIMATED              = 'change:estimated';
    static TYPE_CHANGE_UNESTIMATED            = 'change:unestimated';
    static TYPE_CHANGE_ASSIGNED               = 'change:assigned';
    static TYPE_CHANGE_UNASSIGNED             = 'change:unassigned';
    static TYPE_CHANGE_PLANNED                = 'change:planned';
    static TYPE_CHANGE_UNPLANNED              = 'change:unplanned';
    static TYPE_CHANGE_STARTED                = 'change:started';
    static TYPE_CHANGE_TEAM_REVIEW_STARTED    = 'change:review:started';
    static TYPE_CHANGE_TEAM_REVIEW_FINISHED   = 'change:review:finished';
    static TYPE_CHANGE_CLIENT_REVIEW_STARTED  = 'change:tests:started';
    static TYPE_CHANGE_CLIENT_REVIEW_FINISHED = 'change:tests:finished';
    static TYPE_CHANGE_DEPLOYED               = 'change:deployed';
    static TYPE_CHANGE_SPRINT_ADDED           = 'change:sprint:added';
    static TYPE_CHANGE_SPRINT_REMOVED         = 'change:sprint:removed';
    static TYPE_CHANGE_GOAL_ADDED             = 'change:goal:added';
    static TYPE_CHANGE_GOAL_REMOVED           = 'change:goal:removed';
    static TYPE_CHANGE_RELATED_ADDED          = 'change:related:added';
    static TYPE_CHANGE_RELATED_REMOVED        = 'change:related:removed';
    static TYPE_CHANGE_DISCARDED              = 'change:discarded';
    static TYPE_CHANGE_RESTORED               = 'change:restored';
    static TYPE_CHANGE_MERGED                 = 'change:merged';
    static TYPE_QUOTE_CREATED                 = 'quote:created';
    static TYPE_QUOTE_SUBMITTED               = 'quote:submitted';
    static TYPE_QUOTE_SENT                    = 'quote:sent';
    static TYPE_QUOTE_VIEWED                  = 'quote:viewed';
    static TYPE_QUOTE_ACCEPTED                = 'quote:accepted';
    static TYPE_QUOTE_REFUSED                 = 'quote:refused';
    static TYPE_QUOTE_ADVANCE_BILLED          = 'quote:advance:billed';
    static TYPE_QUOTE_ADVANCE_PAID            = 'quote:advance:paid';
    static TYPE_QUOTE_BILLED                  = 'quote:billed';
    static TYPE_QUOTE_PAID                    = 'quote:paid';
    static TYPE_COMMENT_CREATE                = 'comment:create';
    static TYPE_COMMENT_UPDATE                = 'comment:update';
    static TYPE_ATTACHMENT_ACCESSED           = 'attachment:accessed';
    static TYPE_TRACK_CREATED                 = 'track:created';
    static TYPE_TRACK_UPDATED                 = 'track:updated';
    static TYPE_TRACK_DELETED                 = 'track:deleted';

    _id: string;
    author: User;
    type: string;
    createdAt: Moment;
    updateAt: Moment;
    context: AnyObj;
    project?: Project;
    change?: Change;
    comment?: Comment;
    quote?: Quote;
    commit?: Commit;
    release?: Release;
    /**
     * Transient timeline indent (0 = top-level, 1 = nested under a message).
     * Set by ChangeActivity when imbricating logs with messages; not persisted.
     */
    level?: number;

    constructor(properties: AnyObj) {
        super({});

        this._id       = properties._id;
        this.type      = properties.type;
        this.author    = new User(BaseModel.fromRef(properties.author || {}));
        this.createdAt = moment(properties.createdAt);
        this.updateAt  = moment(properties.updateAt);
        this.context   = properties.context;
        if (typeof properties.level === 'number')
            this.level = properties.level;

        if (properties.project)
            this.project = new Project(BaseModel.fromRef(properties.project));
        if (properties.change)
            this.change = new Change(BaseModel.fromRef(properties.change));
        if (properties.comment)
            this.comment = new Comment(BaseModel.fromRef(properties.comment));
        if (properties.quote)
            this.quote = new Quote(BaseModel.fromRef(properties.quote));
        if (properties.commit)
            this.commit = new Commit(BaseModel.fromRef(properties.commit));
        if (properties.release)
            this.release = new Release(BaseModel.fromRef(properties.release));
    }

    /**
     * Resolve a human-readable label for an audited change field path.
     * Strips language suffixes (e.g. release_notes.title.fr → release_notes.title)
     * so lookups match nested i18n keys under `changes.*`.
     */
    static resolveChangeFieldLabel(field?: string): string {
        if (!field)
            return '';
        const normalized = field.replace(/\.(fr|en|de)$/, '');
        const key = `changes.${normalized}`;
        const translated = t(key);
        return translated === key ? normalized : translated;
    }

    describe(fromOutside?: boolean) {
        const key = fromOutside ? 'logs.outside' : 'logs';

        const params: AnyObj = {
            card: this.change?.localizedTitle(i18n.language),
            quote: this.quote?.slug,
            firstname: this.author?.firstname,
            field: Log.resolveChangeFieldLabel(this.context?.field),
            value: this.context?.value,
            sprint: this.context?.sprint,
            goal: this.context?.goal,
            source: this.context?.source,
            track: this.context?.after?.title
                || this.context?.before?.title
                || this.context?.title
                || '',
        };
        params.link = this.quote ?
            `/quotes/${this.quote?._id}` :
            Project.path(this.project, 'change', this.change?._id);

        if (this.type === Log.TYPE_CHANGE_PATCH) {
            switch (this.context?.field) {
                case 'type': {
                    let valueLabel = t(`projects.${this.context.value}`);
                    // Non-dev cards store the concrete kind in typeSubtype; prefer that label.
                    if (this.context.value === 'none' && this.context.typeSubtype) {
                        const subtypeKey = `changes.subtype.${this.context.typeSubtype}`;
                        const subtypeLabel = t(subtypeKey);
                        if (subtypeLabel !== subtypeKey)
                            valueLabel = subtypeLabel;
                    }
                    return t(`${key}.patch_title_change`, { ...params, value: valueLabel });
                }
                case 'typeSubtype': {
                    const subtypeKey = `changes.subtype.${this.context.value}`;
                    const subtypeLabel = t(subtypeKey);
                    return t(`${key}.patch_title_change`, {
                        ...params,
                        field: Log.resolveChangeFieldLabel('typeSubtype'),
                        value: subtypeLabel !== subtypeKey ? subtypeLabel : this.context.value,
                    });
                }
                case 'typeDomain': {
                    const domainKey = `changes.domain.${this.context.value}`;
                    const domainLabel = t(domainKey);
                    return t(`${key}.patch_title_change`, {
                        ...params,
                        field: Log.resolveChangeFieldLabel('typeDomain'),
                        value: domainLabel !== domainKey ? domainLabel : this.context.value,
                    });
                }
                case 'title.en':
                case 'title.de':
                case 'title.fr':
                    params.field = Log.resolveChangeFieldLabel(this.context.field);
                    return t(`${key}.patch_title_change`, params);
                case 'release_notes.title.en':
                case 'release_notes.title.de':
                case 'release_notes.title.fr':
                    params.field = Log.resolveChangeFieldLabel(this.context.field);
                    return t(`${key}.patch_title_change`, params);
                case 'importance':
                    return t(`${key}.patch_importance`, params);
                case 'developer_notes':
                    return t(`${key}.patch_developer_notes`, params);
                case 'description.en':
                case 'description.de':
                case 'description.fr':
                    params.field = Log.resolveChangeFieldLabel(this.context.field);
                    return t(`${key}.patch_description_change`, params);
                case 'release_notes.description.en':
                case 'release_notes.description.de':
                case 'release_notes.description.fr':
                    params.field = Log.resolveChangeFieldLabel(this.context.field);
                    return t(`${key}.patch_description_change`, params);
                case 'needsAdvance':
                    if (this.context.value)
                        return t(`${key}.needsAdvance_change`, params);
                    return t(`${key}.unNeedsAdvance_change`, params);
                case 'billable':
                    if (this.context.value)
                        return t(`${key}.billable_change`, params);
                    return t(`${key}.unbillable_change`, params);
                case 'budget.status':
                    return t(`${key}.budget_status_changed`, {
                        ...params,
                        value: t(`changes.budget_status_type.${params.value}`)
                    });
                case 'budget.quotedAt':
                case 'budget.quoteAcceptedAt':
                case 'budget.quoteRefusedAt':
                case 'budget.advanceBilledAt':
                case 'budget.advancePaidAt':
                case 'budget.billedAt':
                case 'budget.paidAt':
                    return t(`${key}.changed_${this.context.field}`, {
                        ...params,
                        value: formatMomentDate(params.value, 'LL')
                    });
                case 'replyRecipients':
                    return t(`${key}.reply_recipients_changed`, params);
                default:
                    return t(`${key}.patch_change`, params);
            }
        }

        if (this.type.startsWith('ai:'))
            return this.describeAiAction(key, params);

        switch (this.type) {
            case Log.TYPE_CHANGE_CREATE:
                return t(`${key}.created_change`, params);
            case Log.TYPE_CHANGE_ESTIMATED:
                return t(`${key}.estimated_change`, { ...params, value: this.context.estimate });
            case Log.TYPE_CHANGE_UNESTIMATED:
                return t(`${key}.unEstimated_change`, params);
            case Log.TYPE_CHANGE_PLANNED:
                return t(`${key}.planned_change`, { ...params, deadline: formatMomentDate(this.context.deadline, 'll') });
            case Log.TYPE_CHANGE_UNPLANNED:
                return t(`${key}.unplanned_change`, params);
            case Log.TYPE_CHANGE_ASSIGNED:
                if (this.context.assignee._id !== this.author._id)
                    return t(`${key}.assigned_change`, { ...params, assignee: this.context.assignee.firstname });
                return t(`${key}.took_change`, params);
            case Log.TYPE_CHANGE_UNASSIGNED:
                if (this.context.assignee._id !== this.author._id)
                    return t(`${key}.unassigned_change`, { ...params, assignee: this.context.assignee.firstname });
                return t(`${key}.got_rid_of_change`, params);
            case Log.TYPE_CHANGE_STARTED:
                return t(`${key}.started_change`, params);
            case Log.TYPE_CHANGE_TEAM_REVIEW_STARTED:
                return t(`${key}.started_team_review`, { ...params, ...this.context });
            case Log.TYPE_CHANGE_TEAM_REVIEW_FINISHED: {
                const hasMrApprover = Boolean(this.context?.author?.name);
                const reviewKey = hasMrApprover
                    ? 'finished_team_review'
                    : 'finished_team_review_without_mr';
                return t(`${key}.${reviewKey}`, { ...params, ...this.context });
            }
            case Log.TYPE_CHANGE_CLIENT_REVIEW_STARTED:
                return t(`${key}.started_client_review`, params);
            case Log.TYPE_CHANGE_CLIENT_REVIEW_FINISHED:
                return t(`${key}.finished_client_review`, params);
            case Log.TYPE_CHANGE_DEPLOYED:
                return t(`${key}.deployed_change`, params);
            case Log.TYPE_CHANGE_SPRINT_ADDED:
                return t(`${key}.sprint_added`, params);
            case Log.TYPE_CHANGE_SPRINT_REMOVED:
                return t(`${key}.sprint_removed`, params);
            case Log.TYPE_CHANGE_GOAL_ADDED:
                return t(`${key}.goal_added`, params);
            case Log.TYPE_CHANGE_GOAL_REMOVED:
                return t(`${key}.goal_removed`, params);
            case Log.TYPE_CHANGE_RELATED_ADDED:
                params.related = this.context?.related;
                return t(`${key}.related_added`, params);
            case Log.TYPE_CHANGE_RELATED_REMOVED:
                params.related = this.context?.related;
                return t(`${key}.related_removed`, params);
            case Log.TYPE_CHANGE_DISCARDED:
                return t(`${key}.discarded_change`, params);
            case Log.TYPE_CHANGE_RESTORED:
                if (this.context?.reason === 'reply')
                    return t(`${key}.restored_change_by_reply`, params);
                return t(`${key}.restored_change`, params);
            case Log.TYPE_CHANGE_MERGED:
                return t(`${key}.merged_change`, params);

            case Log.TYPE_QUOTE_CREATED:
                return t(`${key}.quote_created`, params);
            case Log.TYPE_QUOTE_SUBMITTED:
                return t(`${key}.quote_submitted`, params);
            case Log.TYPE_QUOTE_SENT:
                return t(`${key}.quote_sent`, params);
            case Log.TYPE_QUOTE_VIEWED:
                return t(`${key}.quote_viewed`, params);
            case Log.TYPE_QUOTE_ACCEPTED:
                return t(`${key}.quote_accepted`, params);
            case Log.TYPE_QUOTE_REFUSED:
                return t(`${key}.quote_refused`, params);
            case Log.TYPE_QUOTE_ADVANCE_BILLED:
                return t(`${key}.advance_billed`, params);
            case Log.TYPE_QUOTE_ADVANCE_PAID:
                return t(`${key}.advance_paid`, params);
            case Log.TYPE_QUOTE_BILLED:
                return t(`${key}.billed`, params);
            case Log.TYPE_QUOTE_PAID:
                return t(`${key}.paid`, params);
            case Log.TYPE_COMMENT_CREATE:
                return t(`${key}.comment_created`, params);
            case Log.TYPE_COMMENT_UPDATE:
                return t(`${key}.comment_updated`, params);
            case Log.TYPE_ATTACHMENT_ACCESSED: {
                const filename = this.context?.filename || '';
                if (this.context?.source === 'email' && this.comment?._id) {
                    return t(`${key}.attachment_accessed_email`, {
                        ...params,
                        filename,
                        commentId: this.comment._id,
                    });
                }
                if (this.context?.source === 'email')
                    return t(`${key}.attachment_accessed_email_no_ref`, { ...params, filename });
                return t(`${key}.attachment_accessed`, { ...params, filename });
            }
            case Log.TYPE_TRACK_CREATED:
                return t(`${key}.track_created`, params);
            case Log.TYPE_TRACK_UPDATED:
                return t(`${key}.track_updated`, params);
            case Log.TYPE_TRACK_DELETED:
                return t(`${key}.track_deleted`, params);
            default:
                return t(`${key}.unknown_action`, { ...params, type: this.type });
        }
    }

    /**
     * Stable fingerprint for grouping consecutive identical activity entries.
     */
    groupKey(): string {
        const parts = [
            this.author?._id || '',
            this.type,
            this.change?._id || '',
        ];

        if (this.type === Log.TYPE_CHANGE_PATCH) {
            parts.push(String(this.context?.field || ''));
            if (this.context?.field === 'replyRecipients')
                parts.push(JSON.stringify(this.context?.value || {}));
        }

        if (this.type === Log.TYPE_CHANGE_ASSIGNED || this.type === Log.TYPE_CHANGE_UNASSIGNED)
            parts.push(this.context?.assignee?._id || '');

        if (this.type === Log.TYPE_CHANGE_ESTIMATED)
            parts.push(String(this.context?.estimate ?? ''));

        if (this.type === Log.TYPE_CHANGE_PLANNED)
            parts.push(String(this.context?.deadline ?? ''));

        if (this.type === Log.TYPE_CHANGE_SPRINT_ADDED || this.type === Log.TYPE_CHANGE_SPRINT_REMOVED)
            parts.push(this.context?.sprint?._id || '');

        if (this.type === Log.TYPE_CHANGE_GOAL_ADDED || this.type === Log.TYPE_CHANGE_GOAL_REMOVED)
            parts.push(this.context?.goal?._id || '');

        if (this.type === Log.TYPE_CHANGE_RELATED_ADDED || this.type === Log.TYPE_CHANGE_RELATED_REMOVED)
            parts.push(this.context?.related?._id || '');

        if (
            this.type === Log.TYPE_TRACK_CREATED
            || this.type === Log.TYPE_TRACK_UPDATED
            || this.type === Log.TYPE_TRACK_DELETED
        ) {
            parts.push(String(this.context?.track_id || this.context?.after?._id || this.context?.before?._id || ''));
        }

        if (this.type === Log.TYPE_CHANGE_MERGED)
            parts.push(this.context?.source?._id || '');

        if (this.type === Log.TYPE_ATTACHMENT_ACCESSED) {
            parts.push(String(this.context?.attachmentId || ''));
            parts.push(String(this.comment?._id || ''));
            parts.push(String(this.context?.filename || ''));
        }

        if (this.type.startsWith('ai:'))
            this.appendAiGroupKeyParts(parts);

        return parts.join('\0');
    }

    appendAiGroupKeyParts(parts: string[]) {
        parts.push(String(this.context?.stage || ''));
        const aiType = this.type.replace(/^ai:/, '');
        const input = this.context?.input || {};
        const output = this.context?.output || {};

        switch (aiType) {
            case 'ensure_active_track':
            case 'stop_active_track':
                parts.push(String(output.action || ''));
                break;
            case 'spam_suspect_triage':
                parts.push(String(input.decision || ''));
                break;
            case 'sentry_triage':
                parts.push(String(output.status || ''));
                break;
            default:
                break;
        }
    }

    describeAiAction(key: string, params: AnyObj) {
        const aiKey = `${key}.ai`;
        const actor = params.firstname || t(`${aiKey}.assistant`);
        const aiParams = { ...params, firstname: actor };
        const stage = this.context?.stage;
        const input = this.context?.input || {};
        const output = this.context?.output || {};
        const aiType = this.type.replace(/^ai:/, '');

        switch (aiType) {
            case 'create_change':
                return t(`${aiKey}.create_change`, aiParams);
            case 'update_change':
                return t(`${aiKey}.update_change`, aiParams);
            case 'move_change':
                return t(`${aiKey}.move_change`, aiParams);
            case 'start_change':
                if (stage === 'proposal')
                    return t(`${aiKey}.start_change_proposal`, aiParams);
                return t(`${aiKey}.start_change_apply`, aiParams);
            case 'start_change_workflow':
                return t(`${aiKey}.start_change_workflow`, aiParams);
            case 'ensure_active_track':
                if (output.action === 'started')
                    return t(`${aiKey}.ensure_active_track_started`, aiParams);
                if (output.action === 'already_running')
                    return t(`${aiKey}.ensure_active_track_already_running`, aiParams);
                return t(`${aiKey}.ensure_active_track_none`, aiParams);
            case 'stop_active_track':
                if (output.action === 'stopped')
                    return t(`${aiKey}.stop_active_track_stopped`, aiParams);
                return t(`${aiKey}.stop_active_track_none`, aiParams);
            case 'spam_suspect_triage':
                if (input.decision === 'approve')
                    return t(`${aiKey}.spam_suspect_approved`, aiParams);
                if (input.decision === 'reject')
                    return t(`${aiKey}.spam_suspect_rejected`, aiParams);
                return t(`${aiKey}.spam_suspect_triage`, aiParams);
            case 'sentry_triage':
                if (stage === 'proposal')
                    return t(`${aiKey}.sentry_triage_proposal`, aiParams);
                if (output.status === 'accepted')
                    return t(`${aiKey}.sentry_triage_accepted`, aiParams);
                if (output.status === 'refused_and_deleted')
                    return t(`${aiKey}.sentry_triage_refused`, aiParams);
                return t(`${aiKey}.sentry_triage_apply`, aiParams);
            case 'sentry_sync':
                return t(`${aiKey}.sentry_sync`, aiParams);
            case 'sentry_triage_refused':
                return t(`${aiKey}.sentry_triage_refused_legacy`, aiParams);
            case 'link_related_changes':
                return t(`${aiKey}.link_related_changes`, aiParams);
            case 'add_comment':
                return t(`${aiKey}.add_comment`, aiParams);
            case 'reply_to_change':
                return t(`${aiKey}.reply_to_change`, aiParams);
            case 'create_project_label':
                return t(`${aiKey}.create_project_label`, aiParams);
            case 'update_project_label':
                return t(`${aiKey}.update_project_label`, aiParams);
            case 'delete_project_label':
                return t(`${aiKey}.delete_project_label`, aiParams);
            case 'boardUnboardedChanges':
                return t(`${aiKey}.board_unboarded_changes`, aiParams);
            case 'delete_change':
                return t(`${aiKey}.delete_change`, aiParams);
            case 'link_commit_to_change':
                return t(`${aiKey}.link_commit_to_change`, aiParams);
            case 'unlink_commit':
                return t(`${aiKey}.unlink_commit`, aiParams);
            case 'update_change_release_notes':
                return t(`${aiKey}.update_change_release_notes`, aiParams);
            case 'update_release':
                return t(`${aiKey}.update_release`, aiParams);
            case 'reorder_release_changes':
                return t(`${aiKey}.reorder_release_changes`, aiParams);
            case 'sync_project_git':
                return t(`${aiKey}.sync_project_git`, aiParams);
            default:
                return t(`${aiKey}.generic`, { ...aiParams, action: aiType });
        }
    }
}

export default Log;
